import React, { useState, useRef, useEffect } from 'react';
import Scheduler, { AppointmentDragging, Resource } from 'devextreme-react/scheduler';
import ContextMenu from 'devextreme-react/context-menu';
import { cloneDeep, isEmpty } from 'lodash';
import { statusList } from '../../../data/schedulerStatus';
import { viewList } from '../../../data/scheduleViews';
import classes from './SchedulerDashboard.module.css';
import moment from 'moment';
import ResourceCell from './ResourceCell';
import ScheduledAppointment from './ScheduledAppointment';
import './DevExpress.K2A.css';
import Button from 'devextreme/ui/button';
import TagBox from 'devextreme/ui/tag_box';
import DataGrid, { Column, RowDragging } from 'devextreme-react/data-grid';
import AppointmentStatusContextMenuTemplate from './AppointmentStatusContextMenuTemplate';
import { locale } from 'devextreme/localization';
import { connect, useDispatch } from 'react-redux';
import BobModal from '../../../components/BobModal';
import ServiceCallModal from './ServiceCallModal';
import BobLoadingMask from '../../../components/BobLoadingMask';
import AppointmentModal from './AppointmentModal';
import * as ArchitectActions from '../../../actions/architectActions';
import * as CoreActions from '../../../actions/coreActions';
import * as schedulerActions from '../../../actions/schedulerActions';
import { enqueueSnackbar } from '../../../actions/applicationMessageActions';

import { StatusColors } from './AppointmentStatusColors';

const draggingGroupName = 'appointmentsGroup';
const groups = ['technicianId'];

locale(navigator.language);
const dateFormatter = new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'short',
    timeStyle: 'short'
});

const emptyContextMenu = {
    target:           null,
    disabled:         true,
    contextMenuItems: [],
    contextMenuEvent: null
};

const SchedulerDashboard = ({
    bobLoading,
    fetchServiceAppointmentStatuses,
    companyInfo,
    appointmentsList,
    fetchScheduleTechnicians,
    serviceRequestActionModalOpen,
    createServiceCallModalOpen,
    handleToggleCreateServiceCallModal,
    setProgressBarLoading,
    fetchAppointments,
    activeNavCompany,
    serviceAppointmentStatusesList,
    scheduledAppointments,
    unscheduledAppointments,
    isActive,
    technicians,
    updateAppointment,
    ...props
}) => {

    moment.locale(navigator.locale);
    const schedulerRef = useRef(null);
    const unscheduledGridRef = useRef(null);
    const dispatch = useDispatch();

    const [equipmentData] = useState(null);
    const [apptContextMenu, setApptContextMenu] = useState(emptyContextMenu);
    const [appointments, setAppointments] = useState([]);
    const [resourcesList, setResourcesList] = useState([]);
    const [isAppointmentModalOpen, handleToggleAppointmentModal] = useState(false);
    const [appointmentData, setAppointmentData] = useState({});

    useEffect(() => {
        setAppointments(scheduledAppointments);
    }, [scheduledAppointments]);

    useEffect(() => {
        async function init() {
            setProgressBarLoading(true);

            let promises = [];

            promises.push(fetchAppointments(activeNavCompany.id, 0));
            promises.push(fetchAppointments(activeNavCompany.id, 1));
            promises.push(fetchServiceAppointmentStatuses());

            if (isEmpty(technicians)) {
                promises.push(fetchScheduleTechnicians(activeNavCompany.id));
            }

            await Promise.all(promises.map(p => p.catch(err => err)));
            
            setProgressBarLoading(false);
        }

        if (activeNavCompany && activeNavCompany.id) {
            init();
        }
    }, [activeNavCompany.id]);

    const onContextMenuClick = (e) => {
        // Todo: update status of appointment
        setAppointments([...appointments]);
    };

    const onAppointmentAdd = async (e) => {
        if (isEmpty(resourcesList)) {
            dispatch(enqueueSnackbar('Please select a technician first', 'warning'));
            return;
        }

        if (e.itemData.estimateHours === 0) {
            e.itemData.estimateHours = 1;
        }

        let appointment = cloneDeep(e.itemData);
        appointment.enddatetime = new Date(moment(appointment.startdatetime).add(appointment.estimateHours, 'h'));
        appointment.technicians = {userIds: []};
        appointment.technicians.userIds.push(appointment.technicianId);

        const isUpdated = await updateAppointment(activeNavCompany.id, appointment);

        if (isUpdated) {
            setAppointments(appointments => [...appointments, appointment]);

            unscheduledAppointments = unscheduledAppointments.splice(e.fromIndex, 1);
            unscheduledGridRef.current.instance.refresh();
            schedulerRef.current.instance.getWorkSpace()._dimensionChanged();
        } else {
            return;
        }
    };

    useEffect(() => {
        if (activeNavCompany.id) {
            let resources = [];
            let list = JSON.parse(localStorage.getItem(`ScheduleSelectedResources-${activeNavCompany.id}`));

            if (list) {
                resources = technicians.filter(({ id }) => list.some(item => item.id === id));
                setResourcesList(resources);
            } else {
                setResourcesList(technicians);
            }
            schedulerRef.current.instance.getWorkSpace()._dimensionChanged();
        }
    }, [technicians]);

    const unscheduledAppointmentCellRender = (cellData) => {
        locale(navigator.language);
        const dateFormatter = new Intl.DateTimeFormat(navigator.language, {
            dateStyle: 'short',
            timeStyle: 'short'
        });

        let unscheduledApptBarColor = {
            padding:    '5px',
            borderLeft: 'solid 8px yellow'
        };

        let barColor = StatusColors.find(c => c.id === cellData.data.appointmentStatusId).color;
        unscheduledApptBarColor.borderLeftColor = barColor;
        let apptDate = cellData.data.startdatetime ? new Date(cellData.data.startdatetime) : null;
        let statusData = serviceAppointmentStatusesList.find(s => s.id === cellData.data.appointmentStatusId);

        const appointmentStatus = statusData ? statusData.label : 'OPEN';
        return (
            <div>
                <div className={classes.unassignedAppointmentWrapper}>
                    <div style={unscheduledApptBarColor}>
                        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            {cellData.data.type === 'ServiceCall' ? 'Service Call' : cellData.data.type === 'Job' ? 'Job' : 'Tech Activity'}
                        </div>
                        <div style={{ fontSize: '10px', fontWeight: 'bold' }}>{cellData.data.resourceName}</div>
                        <div style={{ fontSize: '10px' }}>Start On - {dateFormatter.format(apptDate)} ({cellData.data.estimateHours} Hrs)</div>
                        <div style={{ fontSize: '10px' }}>Status - {appointmentStatus}</div>
                    </div>
                    <div id={'apptCell-' + cellData.data.id} className={classes.hideApptDetail}>
                        <div>
                            <div style={{ fontSize: '10px' }}>
                                {cellData.data.type === 'ServiceCall' ? 'Service Call' : cellData.data.type === 'Job' ? 'Job' : 'Tech Activity'} - {cellData.data.workOrder}:{cellData.data.number}
                            </div>
                            <div style={{ fontSize: '10px' }}>
                                Description<div>{cellData.data.description}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const unscheduledCellClick = (e) => {
        if (e.data) {
            let cellDiv = document.getElementById('apptCell-' + e.data.id);
            if (cellDiv.style.display === 'none' || cellDiv.style.display === '') {
                cellDiv.style.display = 'block';
            }
            else {
                cellDiv.style.display = 'none';
            }
        }
    };


    const onContextMenuHiding = () => {
        setApptContextMenu({ disabled: true, contextMenuItems: [] });
    };

    const onAppointmentClick = (appointment) =>{
        setAppointmentData(appointment);
        handleToggleAppointmentModal(true);
    };

    const onAppointmentContextMenu = (e) => {
        const menu = {
            target:           '.dx-scheduler-appointment',
            disabled:         false,
            contextMenuItems: statusList,
            contextMenuEvent: e
        };
        setApptContextMenu(menu);
        schedulerRef.current.instance.getWorkSpace()._dimensionChanged();
    };

    const onValueChanged = (e) => {
        setResourcesList(e.value);

        localStorage.setItem(`ScheduleSelectedResources-${activeNavCompany.id}`, JSON.stringify(e.value));
        schedulerRef.current.instance.getWorkSpace()._dimensionChanged();
    };

    const onSchedulerContentReady = (e) => {
        // Add today button
        const todayButton = document.getElementById('schedulerTodayButton');
        const technicianButton = document.getElementById('schedulerTechnicianButton');

        if (!todayButton) {
            let element = document.querySelectorAll('.dx-scheduler-navigator');
            const container = document.createElement('span');
            container.id = 'schedulerTodayButton';
            container.style.marginLeft = '15px';

            element[0].appendChild(container);

            new Button(container, {
                text:        'Today',
                stylingMode: 'outlined',
                onClick:     () => {
                    schedulerRef.current.instance.option('currentDate', new Date());
                }
            });
        }

        if (!technicianButton) {
            let element = document.querySelectorAll('.dx-scheduler-header');
            const techContainer = document.createElement('span');
            techContainer.id = 'schedulerTechnicianButton';
            techContainer.style.marginLeft = '1140px';
            techContainer.style.marginTop = '6px';
            techContainer.style.marginRight = '220px';

            element[0].appendChild(techContainer);

            new TagBox(techContainer, {
                value:                 resourcesList,
                items:                 technicians,
                applyValueMode:        'useButtons',
                searchEnabled:         true,
                onValueChanged:        (e) => onValueChanged(e),
                displayExpr:           'fullName',
                maxDisplayedTags:      1,
                placeholder:           'Select technicians...',
                showSelectionControls: true,
                showDropDownButton:    true
            });
        }
        schedulerRef.current.instance.getWorkSpace()._dimensionChanged();
    };

    const onAppointmentRender = (e) => {
        let barColor = StatusColors.find(c => c.id === e.appointmentData.appointmentStatusId).color;
        return <ScheduledAppointment appointmentData={e.appointmentData} statusColor={barColor} />;
    };

    const onAppointmentUpdate = async event => {
        schedulerRef.current.instance.getWorkSpace()._dimensionChanged();
        await updateAppointment(activeNavCompany.id, event.newData);
        schedulerRef.current.instance.getWorkSpace()._dimensionChanged();
    };

    const updateUnscheduledAppointment = e => {
        setAppointmentData(e.data);
        handleToggleAppointmentModal(true);
    };

    return (
        <div className="dx-viewport">
            <div className={classes.ScheduleViewDiv}>
                <div className={classes.ScheduleBoardDiv}>
                    <Scheduler
                        ref={schedulerRef}
                        height="75vh"
                        width="inherit"
                        dataSource={appointments}
                        views={viewList}
                        groups={groups}
                        startDayHour={6}
                        endDayHour={18}
                        crossScrollingEnabled={true}
                        resourceCellRender={ResourceCell}
                        appointmentRender={onAppointmentRender}
                        editing={{ allowAdding: false }}
                        onContentReady={onSchedulerContentReady}
                        onAppointmentContextMenu={onAppointmentContextMenu}
                        showAllDayPanel={false}
                        onAppointmentClick={(e) => onAppointmentClick(e)}
                        onAppointmentUpdating={(e) => onAppointmentUpdate(e)}
                        useDropDownViewSwitcher={true}
                        endDateExpr={'enddatetime'}
                        startDateExpr={'startdatetime'}
                    >
                        <Resource
                            dataSource={resourcesList}
                            fieldExpr={'technicianId'}
                            valueExpr={'Id'}
                            label={'technician'}
                            allowMultiple={false}
                        />
                        <AppointmentDragging
                            group={draggingGroupName}
                            onAdd={onAppointmentAdd}
                        />
                    </Scheduler>
                    <ContextMenu
                        dataSource={statusList}
                        width={175}
                        target={apptContextMenu.target}
                        disabled={apptContextMenu.disabled}
                        onItemClick={onContextMenuClick}
                        onHiding={onContextMenuHiding}
                        itemRender={AppointmentStatusContextMenuTemplate}
                    />
                </div>
                <div className={classes.UnscheduledDiv}>
                    <DataGrid
                        ref={unscheduledGridRef}
                        height="75vh"
                        width="280px"
                        dataSource={unscheduledAppointments}
                        onCellClick={unscheduledCellClick}
                        onRowDblClick={updateUnscheduledAppointment}
                    >
                        <Column
                            dataField="workOrder"
                            caption="Unscheduled Appointments"
                            allowEditing={false}
                            allowGrouping={false}
                            allowFiltering={false}
                            allowSearch={false}
                            allowSorting={false}
                            allowHeaderFiltering={false}
                            cellRender={unscheduledAppointmentCellRender}
                        />
                        <RowDragging
                            group={draggingGroupName}
                            dragRender={(e, container) => {
                                let apptResource = {};
                                if (technicians.find(r => r.Id === e.itemData.technicianId)) {
                                    apptResource = technicians.find(r => r.Id === e.itemData.technicianId);
                                } else {
                                    apptResource.name = e.itemData.fullName;
                                }                               

                                return (
                                    <div className={classes.UnscheduledDragTemplate}>
                                        <div>Work Order: {e.itemData.workOrder}</div>
                                        <div>Scheduled Time: {e.itemData.startdatetime ? dateFormatter.format(new Date(e.itemData.startdatetime)) : 'Unscheduled'}</div>
                                        <div>Estimate Hours: {e.itemData.estimatedHours}</div>
                                        <div>Current Resource: {apptResource.name}</div>
                                    </div>
                                );
                            }}
                        />
                    </DataGrid>
                </div>
                {
                    serviceRequestActionModalOpen ?
                        <BobModal
                            title={'New Service Call'}
                            hidebuttons={true}
                            isRemoveFooter={true}
                            onClose={() => {
                                handleToggleCreateServiceCallModal(false);
                            }}
                        >
                            <ServiceCallModal
                                equipment={equipmentData}
                                onClose={() => {
                                    handleToggleCreateServiceCallModal(false);}}
                            />
                        </BobModal> : ''
                }
                {
                    isAppointmentModalOpen ?
                        <BobModal
                            title={'Appointment'}
                            hidebuttons={true}
                            isRemoveFooter={true}
                            onClose={() => {
                                handleToggleAppointmentModal(false);
                            }}
                        >
                            <AppointmentModal
                                appointment={appointmentData}
                                onClose={() => {
                                    handleToggleAppointmentModal(false);}}
                            />
                        </BobModal> : ''
                }
                {
                    bobLoading.includes('UpdateAppointment') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeNavCompany:               state.bob.activeNavCompany,
        bobLoading:                     state.bob.bobLoading,
        serviceAppointmentStatusesList: state.architect.serviceAppointmentStatusesList,
        companyInfo:                    state.companyInfo,
        scheduledAppointments:          state.scheduler.scheduledAppointments,
        technicians:                    state.scheduler.technicians,
        unscheduledAppointments:        state.scheduler.unscheduledAppointments
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...schedulerActions,
    ...CoreActions
};

export default connect(mapStateToProps, mapDispatchToProps)(SchedulerDashboard);
